import type {
  APIConfig,
  Constructor,
  HttpConfig,
  TransportConfig,
} from './api.types';
import { useHttp } from './http';

interface Transports {
  http: Map<string, ReturnType<typeof useHttp>>;
  ws: Map<string, unknown>;
  _defaultHttpTransport: ReturnType<typeof useHttp> | null;
  _defaultWebSocketTransport: unknown | null;
}

const transports: Transports = {
  http: new Map<string, ReturnType<typeof useHttp>>(),
  ws: new Map<string, any>(),

  _defaultHttpTransport: null,
  _defaultWebSocketTransport: null,
};

type HttpService = ReturnType<typeof useHttp>['httpService'];

const queueForHttpSetup = new Set<() => void>();

function queueResolver() {
  if (queueForHttpSetup.size > 0) {
    for (const resolver of queueForHttpSetup) {
      resolver();
    }
  }
}

export function setupHttp(
  httpConfig: HttpConfig,
  config: TransportConfig = {
    default: false,
    version: `http-api-${transports.http.size + 1}`,
  },
): HttpService {
  config.version ??= `http-api-${transports.http.size + 1}`;
  config.default ??= false;

  const http = useHttp(httpConfig as any, config.interceptors);
  transports.http.set(config.version, http);
  if (config.default) {
    transports._defaultHttpTransport = http;
  }

  console.log(`✅ HTTP transport ${config.version} initialized.`);
  queueResolver();

  return http.httpService;
}

function getHttpTransport(version?: string): ReturnType<typeof useHttp> | null {
  return version ? transports.http.get(version) ?? null : transports._defaultHttpTransport;
}

export function defineApi<T extends Constructor>(
  service: T,
  config: APIConfig = {
    protocol: 'http',
    serviceConstructorArgs: [],
  },
): () => InstanceType<T> {
  config.protocol ??= 'http';
  config.serviceConstructorArgs ??= [];

  if (config.protocol !== 'http') {
    throw new Error('Only HTTP protocol is supported.');
  }

  // Create a placeholder for the service instance
  let serviceInstance: InstanceType<T> | null = null;
  let initializationPromise: Promise<void> | null = null;

  // Initialize the service asynchronously
  const initialize = async () => {
    if (transports.http.size === 0 && !transports._defaultHttpTransport) {
      console.log(`⌛️ defineApi for ${service.name} waiting for HTTP transport setup...`);

      let resolve: () => void;
      const promise = new Promise<void>((res) => {
        resolve = res;
      });

      const resolver = () => {
        const httpTransport = getHttpTransport(config.version);
        if (httpTransport) {
          resolve();
          queueForHttpSetup.delete(resolver);
        }
      };

      queueForHttpSetup.add(resolver);

      await promise;
    }

    const httpTransport = getHttpTransport(config.version);

    if (!httpTransport) {
      throw new Error('No HTTP transport initialized. Setup HTTP transport first.');
    }

    serviceInstance = httpTransport.defineService(service, config.serviceConstructorArgs);
  };

  // Start initialization immediately
  initializationPromise = initialize();

  // Return a function that provides access to the service
  return () => {
    // If service is already initialized, return it
    if (serviceInstance) {
      return serviceInstance;
    }

    // If service is not initialized yet, create a proxy that will wait for initialization
    // This allows synchronous usage in components while handling async initialization internally
    return new Proxy({} as InstanceType<T>, {
      get(target, prop) {
        return async (...args: any[]) => {
          // Wait for initialization to complete if it hasn't already
          if (initializationPromise) {
            await initializationPromise;
            initializationPromise = null;
          }

          if (!serviceInstance) {
            throw new Error(`❌ Service ${service.name} failed to initialize`);
          }

          // Call the actual method on the service instance
          return (serviceInstance as any)[prop](...args);
        };
      },
    });
  };
}
